<template>
	<div
		:class="{
			brand: true,
			'had-action': !!hadAction,
			spinning: !!spinning && !full,
			flip: !!flip && !full,
			'flip-mobile': false,
			full: full,
			slider: !!slider,
			'has-top-label': !!topLabel,
			connected: isConnected,
			disabled: disabled,
			tabbed: tabbed,
			'has-options': hasOptions,
			landscape: !full,
			'show-breakdown': showBreakdown,
			['match-level-' + matchLevel]: !!matchLevel,
		}"
		:ref="'brand-' + brand.brand_id"
		:id="'brand-tile-' + brand.brand_id"
		:data-id="brand.brand_id">
		<div v-if="brand.justHappened" class="just-happened-overlay">
			<div class="just-happened-overlay-inner">
				<div class="brand-top">
					<div class="img-wrap-outer" :style="logoContainerStyles">
						<BrandLogo :brandInfo="brand" :disabled="false" :square="true" />
					</div>
				</div>
				<p class="just-happened-message" v-html="brand.justHappened.message" />
			</div>
		</div>

		<span :class="{ 'top-label': true }" v-if="topLabel"
			><Icon :type="topLabel.icon" v-if="topLabel.icon" colour="#ffffff" class="svg" /> {{ topLabel.label }}</span
		>

		<span v-if="isNew" :class="{ new: true }">{{ brand.new_label }}</span>

		<span :class="{ 'category-label': true }" v-if="baseType !== 'connected' || !showingConnectedInfo">{{
			brand.brand_category?.label?.[0]?.label?.split("(")[0]?.trim()?.split("e.g")[0]?.trim()
		}}</span>

		<div class="tab-wrap" v-if="tabs">
			<Tabs
				:tabs="tabs"
				:key="tabs[tab].field + tabs[tab].value"
				@tabChange="tabChange"
				:startTab="tab"
				tabStyle="modal"
				background="#eff3f2"
				:uniqueName="'brand-modal-' + brand.brand_id" />
		</div>

		<div class="connected-info" v-if="showingConnectedInfo">
			<div class="img-contact">
				<div class="connected-brand-summary">
					<h3 class="underline connected-brand-summary__eyebrow"><span>You connected with</span></h3>
					<div class="connected-brand-summary__panel" v-if="!brand.justHappened">
						<div class="brand-top">
							<div class="img-wrap-outer" :style="logoContainerStyles">
								<BrandLogo :brandInfo="brand" :disabled="disabled" :square="true" />
							</div>

							<div class="brand-top-info">
								<h2 class="name">
									<span>{{ brand.brand_name }}</span>
								</h2>
								<p class="connected-date" v-if="connectedDateLabel">{{ connectedDateLabel }}</p>
							</div>
						</div>
					</div>
				</div>

				<div class="connect-with-info">
					<p class="connect-with-info__label">Contact</p>
					<p class="connect-with-info__name">
						<strong>{{ brand.fname }} {{ brand.sname }}</strong>
					</p>
					<p class="connect-with-info__email">
						<a :href="'mailto:' + brand.email">{{ brand.email }}</a>
					</p>
					<p class="connect-with-info__note">Take this off PartnerUp and introduce yourself directly.</p>
				</div>
			</div>

			<div class="collab-types-wrap">
				<h3 class="underline connected-brand-summary__eyebrow"><span>Need some ideas? We recommend...</span></h3>

				<CollabTypes 
					:mini="true" 
					:filterByIds="[833,840,1476,847,853,868,865,874,877,880,1305]" 
					:randomFrom="4" 
					:showTCs="false"
				/>

				<h4 class="more-help"><span @click="handleMoreHelp">We have more help here</span></h4>
			</div>
		</div>

		<div class="brand-layout" v-if="!showingConnectedInfo && !showBreakdown" :id="'brand-' + id">
			<div class="brand-layout__sections">
				<div
					v-if="normalizedType === 'requests' && (brand.requestType === 'received' || isSnoozed) && !!expires"
					:class="{ expires: true, snoozed: isSnoozed }">
					<Icon type="snooze" class="svg" colour="#679DA2" title="Snoozed" v-if="isSnoozed" />
					<span class="hint"></span>
					<span v-html="expires.remaining"></span>
				</div>

				<div :class="{ 'brand-top': true }" v-if="!brand.justHappened">
					<div :class="{ 'img-wrap-outer': true, clickable: viewBrandAllowed }" :style="logoContainerStyles" @click="viewBrand">
						<BrandLogo :brandInfo="brand" :disabled="disabled" :square="true" />

						<p class="label clickable" v-if="!showingBrandNames && isActive && !brandInfoPublic" @click="toggleShowInfo">
							Your brand is hidden, so you can't view other brand names. You can still send requests, and if accepted, their name and
							contact details will be shared.
						</p>

						<p class="illustration-only" v-if="full && !brandImg.isLogo">Image for illustration only</p>
					</div>

					<div :class="{ 'brand-top-info': true, 'is-masked-name': !showingActualBrandName }">
						<h2 class="name standard" v-if="brand.status === 'requested' && brand.requestType === 'received'">
							<span @click="viewBrand" :class="{ disabled: !brand.brand_website }">
								<span v-if="hideBrandName">{{ hideBrandNameMessage }}</span>
								<span class="req-from-text" v-if="!hideBrandName">A request from </span><br v-if="!hideBrandName" />
								<strong v-if="!brand.hideBrandName">{{ brand.brand_name }}</strong>
							</span>
						</h2>

						<h2 class="name" v-if="brand.status === 'connected'" :title="brand.brand_name__title || ''">
							<span @click="viewBrand" :class="{ disabled: !brand.brand_website }">{{ brand.brand_name }}</span>
						</h2>
						<p class="connected-date" v-if="brand.status === 'connected' && connectedDateLabel">{{ connectedDateLabel }}</p>

						<h2
							class="name"
							v-html="displayBrandName.label"
							v-if="brand.status !== 'connected' && !(brand.status === 'requested' && brand.requestType === 'received')"
							:title="brand.brand_name__title || ''" />

						<div class="brand-head-badge clickable" v-for="badge in brand.badges" :key="badge.key">
							<Icon :type="badge.key" class="svg" colour="rgba(210, 0, 0, 0.6)" @click="badgeClick(badge.key)" />
							<span v-html="badge.label" @click="badgeClick(badge.key)"></span>
						</div>
						<div class="brand-head-badge clickable" v-if="!isActive && full && !brand.show_brand_name && isApproved">
							<Icon type="matches" class="svg" colour="rgba(210, 0, 0, 0.6)" @click="subscribe" />
							<span @click="subscribe">Start your subscription to see all brand names.</span>
						</div>
						<div class="brand-head-badge" v-if="!isActive && full && !brand.show_brand_name && !isApproved">
							<Icon type="profile" class="svg" colour="rgba(210, 0, 0, 0.6)" />
							<span>Your account is awaiting approval by the team.</span>
						</div>
					</div>
				</div>

				<div class="brand-meta-panel-row" v-if="showBrandMetaPanel">
					<div
						:class="{ 'brand-meta-panel': true, 'hide-on-mobile': full && showWhatsNext && showConnectWindow, [topSnippetPanelKey]: true }"
						v-if="showTopSignalPanel">
						<template v-if="topBrandMetaSnippet">
							<span class="brand-meta-panel__label" v-if="topSnippetPanelLabel">{{ topSnippetPanelLabel }}</span>
							<div
								v-if="topSnippetIsActivePitch"
								:class="{ 'brand-meta-panel__active-pitch': true, clickable: topSnippetIsClickable }"
								@click="openTopSnippet">
								<div class="brand-meta-panel__active-pitch-copy">
									<h3 class="brand-meta-panel__title">{{ topSnippetPanelTitle }}</h3>
									<p class="brand-meta-panel__value" v-if="topSnippetPanelValue">{{ topSnippetPanelValue }}</p>
									<span class="brand-meta-panel__cta" v-if="topSnippetIsClickable">View opportunity</span>
								</div>

								<div class="brand-meta-panel__active-pitch-image" v-if="topSnippetPanelImageUrl">
									<img :src="topSnippetPanelImageUrl" :alt="topSnippetPanelTitle" />
								</div>
							</div>

							<div
								v-else-if="topSnippetIsCollaborationHistory"
								:class="{ 'brand-meta-panel__active-pitch': true }"
								@click="openTopSnippet">
								<div class="brand-meta-panel__active-pitch-copy">
									<h3 class="brand-meta-panel__title">{{ topSnippetPanelTitle }}</h3>
									<p class="brand-meta-panel__value" v-if="topSnippetPanelValue">{{ topSnippetPanelValue }}</p>
									<a
										:href="topSnippetPanelExternalLink"
										class="brand-meta-panel__cta"
										target="_blank"
										rel="noopener noreferrer"
										v-if="topSnippetIsClickable"
										>View collab</a
									>
								</div>

								<div class="brand-meta-panel__active-pitch-image collab" v-if="topSnippetPanelImageUrl">
									<a :href="topSnippetPanelExternalLink" target="_blank" rel="noopener noreferrer" v-if="topSnippetIsClickable">
										<img :src="topSnippetPanelImageUrl" :alt="topSnippetPanelTitle" />
									</a>
									<img v-else :src="topSnippetPanelImageUrl" :alt="topSnippetPanelTitle" />
								</div>
							</div>

							<p class="brand-meta-panel__value" v-else>
								{{ topSnippetPanelValue || topSnippetPanelTitle }}
							</p>
						</template>
					</div>

					<div
						:class="{ 'brand-meta-panel': true, 'hide-on-mobile': full && showWhatsNext && showConnectWindow, doi: true }"
						v-if="dateOfInterestItemsProcessed.length > 0">
						<div class="brand-meta-panel__doi-column">
							<p class="brand-meta-panel__doi-title">Their dates of interest...</p>
							<div class="date-interest-row">
								<span
									v-for="dateInfo in dateOfInterestItemsProcessed"
									:key="'doi-item-' + dateInfo.id"
									:class="{ 'date-interest-row__item': true, 'is-current-user-match': dateInfo.isCurrentUserMatch }"
									tabindex="0"
									role="button"
									@click="onDateOfInterestQuickBrief(dateInfo, $event)"
									@keydown.enter.prevent="onDateOfInterestQuickBrief(dateInfo, $event)"
									@keydown.space.prevent="onDateOfInterestQuickBrief(dateInfo, $event)">
									<span class="date-interest-thumb__img">
										<img :src="dateInfo.thumb" :alt="dateInfo.label" />
									</span>
									<span class="date-interest-tooltip" v-html="dateInfo.tooltipLabel"></span>
								</span>
							</div>
						</div>
					</div>
				</div>

				<div :class="{ 'brand-primary-content': true, content: true, 'hide-on-mobile': full && showWhatsNext && showConnectWindow }">
					<div
						:class="{
							'more-about-wrap': true,
							'button-wrap': true,
							center: !(brand.has_connection_message && !!full),
							even: !!brand.has_connection_message,
						}"
						v-if="full && (brand.status === 'requested' || brand.status === 'connected') && (brand.requestType === 'received' || brand.status === 'connected')">
						<span
							v-if="brand.has_connection_message && !!full"
							class="button has-svg mini no-case flip-colours no-margin"
							@click="viewMessage(brand.connection_message || null)"
							><span><Icon type="email" colour="#fff" :class="{ svg: true }" /> <span>Read their message</span></span></span
						>
					</div>

					<div class="info-wrap">
						<p v-if="brandDescription" class="brand-description">
							<span class="description"
								>{{ brandDescription }}<span v-if="hasMoreDescription && !showMoreDescription">…</span>  <span
									class="more"
									@click="showMoreDescription = !showMoreDescription"
									v-html="showMoreDescription ? 'show less' : 'show more'"
									v-if="hasMoreDescription"></span
							></span>
						</p>

						<p v-if="brand.service && !!brand.brand_name && full"><span class="label">Product/Service:</span> {{ brand.service }}</p>

						<p v-if="brand.customer_gender?.label && full">
							<span class="label">Customer Gender: </span>
							<BrandLabel :full="full" :disabled="disabled" fieldName="customer_gender" :brand="brand" :matchBrand="matchBrand" />
						</p>

						<p v-if="brand.customer_age?.label && full">
							<span class="label">Customer Age: </span>
							<BrandLabel :full="full" :disabled="disabled" fieldName="customer_age" :brand="brand" :matchBrand="matchBrand" />
						</p>

						<div class="tags-section" v-if="showingBookmarked || (brand.tags && brand.tags.length > 0)">
							<div class="tags-header" v-if="showingBookmarked">
								<div class="tags-wrap" v-if="brand.tags && brand.tags.length > 0">
									<span
										v-for="tag in brand.tags"
										:key="tag.id"
										class="button mini tag-button no-margin has-remove"
										:style="{ backgroundColor: tag.color, borderColor: tag.color }"
										@click="$emit('tag-clicked', tag)">
										<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M96.5 160L96.5 309.5C96.5 326.5 103.2 342.8 115.2 354.8L307.2 546.8C332.2 571.8 372.7 571.8 397.7 546.8L547.2 397.3C572.2 372.3 572.2 331.8 547.2 306.8L355.2 114.8C343.2 102.7 327 96 310 96L160.5 96C125.2 96 96.5 124.7 96.5 160zM208.5 176C226.2 176 240.5 190.3 240.5 208C240.5 225.7 226.2 240 208.5 240C190.8 240 176.5 225.7 176.5 208C176.5 190.3 190.8 176 208.5 176z"/></svg> 
										{{ tag.name }}
										<span class="remove-tag" @click.stop="removeTag(tag.id)">×</span>
									</span>
								</div>
								<button class="button mini edit-tags-button no-margin" @click="editTags" title="Add/Edit Tags">
									<Icon type="edit" class="svg edit-icon" colour="#679DA2" />
									{{ brand.tags && brand.tags.length > 0 ? "Edit" : "Add" }} Tags
								</button>
							</div>
							<div class="tags-wrap" v-else-if="brand.tags && brand.tags.length > 0">
								<span
									v-for="tag in brand.tags"
									:key="tag.id"
									class="button mini tag-button no-margin has-remove"
									:style="{ backgroundColor: tag.color, borderColor: tag.color }"
									@click="$emit('tag-clicked', tag)">
									<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M96.5 160L96.5 309.5C96.5 326.5 103.2 342.8 115.2 354.8L307.2 546.8C332.2 571.8 372.7 571.8 397.7 546.8L547.2 397.3C572.2 372.3 572.2 331.8 547.2 306.8L355.2 114.8C343.2 102.7 327 96 310 96L160.5 96C125.2 96 96.5 124.7 96.5 160zM208.5 176C226.2 176 240.5 190.3 240.5 208C240.5 225.7 226.2 240 208.5 240C190.8 240 176.5 225.7 176.5 208C176.5 190.3 190.8 176 208.5 176z"/></svg> 
									{{ tag.name }}
									<span class="remove-tag" @click.stop="removeTag(tag.id)">×</span>
								</span>
							</div>
						</div>

						<div class="followings" v-if="socials.length > 0 || !!brand.brand_website">
							<div class="following" v-if="!!brand.brand_website">
								<div>
									<div class="icon-wrap">
										<div class="icon svg website">
											<Icon type="website" :class="{ svg: true, website: true }" colour="rgba(0, 0, 0, 0.56)" />
										</div>
									</div>
									<span class="label">
										<span class="wrap">
											<a :href="brand.brand_website" target="_blank" rel="noopener noreferrer">{{
												brand.brand_website_label || brand.brand_website
											}}</a>
										</span>
									</span>
								</div>
							</div>
							<div :class="{ following: true }" v-for="(social, socialIndex) in socials" :key="'social-' + socialIndex">
								<div v-if="brand[social.vars].label && brand[social.vars].label.length > 0">
									<div class="icon-wrap">
										<Icon :type="social.icon" :class="{ svg: true, [social.icon]: true, disabled: disabled }" />
									</div>
									<a v-if="social.href" :href="social.href" target="_blank" rel="noopener noreferrer" class="label social-link">
										<BrandLabel
											:full="full"
											:disabled="disabled"
											:fieldName="social.vars"
											:brand="brand"
											:matchBrand="matchBrand"
											:linked="true"
											:append="social.label" />
									</a>
									<BrandLabel
										v-else
										:full="full"
										:disabled="disabled"
										:fieldName="social.vars"
										:brand="brand"
										:matchBrand="matchBrand"
										class="label"
										:append="social.label" />
								</div>
							</div>
						</div>

						<span class="vegan-wrap" v-if="brand.veg.value[0] === 1">
							<p><Icon type="leaf" colour="#679DA2" class="vegan-icon" />Vegan brand</p>
						</span>
					</div>

					<span class="report" @click="reportBrand" v-if="shouldShowReportBrand">Report this brand for incorrect use of PartnerUp</span>
				</div>

				<div class="brand-secondary-content brand-secondary-content--whats-next content" v-if="showWhatsNext && showConnectWindow">
					<hr class="hide-on-desktop" />

					<h2>What to do next...</h2>

					<p v-if="!limitedNoSubscription">
						Please choose to either <strong>Connect</strong>, <strong>Snooze</strong> or <strong>Decline</strong>. This keeps the other
						brand in the loop.
					</p>

					<div v-if="limitedNoSubscription" class="limited-no-subscription">
						<p>
							Please upgrade to respond to this request. It will expire in <em>{{ expires.remainingDays }}</em
							>.
						</p>

						<p v-if="subscription.discountInfo" v-html="subscription.discountInfo.message"></p>

						<span class="button no-margin grey hover-secondary mini" @click="subscribe">Click here to upgrade</span>
					</div>

					<hr class="hide-on-desktop" />

					<div class="connect-message hide-on-desktop">
						<div class="heading-btn">
							<h2>Check out all their info...</h2>
							<div>
								<span class="button no-margin no-case flip-colours mini has-svg" @click="toggleConnectWindow"
									><span
										><Icon type="profile" class="svg" colour="#ffffff" />
										<span>All about {{ brand.brand_name || "this brand" }}</span></span
									></span
								>
							</div>
						</div>
					</div>

					<hr />

					<div class="connect-message">
						<div class="heading-btn">
							<h2>Connect: Love at first sight?</h2>
							<div>
								<span class="button no-margin no-case flip-colours mini has-svg" @click="action(brand.brand_id, 'confirm', false)"
									><span
										><Icon type="connections" class="svg" colour="#ffffff" />
										<span>{{ buttonDefaults.labels.confirm }}</span></span
									></span
								>
							</div>
						</div>

						<p>
							Hit <strong class="form-default">{{ buttonDefaults.labels.confirm }}</strong> and we'll share their contact details.
							<strong>{{ brand.brand_name }}</strong> will also receive an email with your contact information and you can get started
							on your collab.
						</p>
					</div>

					<hr v-if="!!brand.snoozeable" />

					<div class="connect-message" v-if="!!brand.snoozeable">
						<div class="heading-btn">
							<h2>Snooze: Interested but no time right now?</h2>
							<div>
								<span class="button no-margin no-case flip-colours mini has-svg" @click="action(brand.brand_id, 'snooze', false)"
									><span
										><Icon type="snooze" class="svg" colour="#ffffff" /> <span>{{ buttonDefaults.labels.snooze }}</span></span
									></span
								>
							</div>
						</div>

						<p>
							If you like the look of the brand, but don't have the capacity to collab right now, no sweat!
							<strong class="form-default">{{ buttonDefaults.labels.snooze }}</strong> this request for up to
							<strong>2 months</strong> (you can Connect or Decline within this period). <strong>{{ brand.brand_name }}</strong> will be
							notified of your <strong class="form-default">{{ buttonDefaults.labels.snooze }}</strong
							>. Please note, they may cancel this request if they can't wait.
						</p>
					</div>

					<hr />

					<div class="connect-message">
						<div class="heading-btn">
							<h2>Decline: If the brand isn't the right fit it's no problem.</h2>
							<div>
								<span class="button no-margin no-case flip-colours mini has-svg" @click="action(brand.brand_id, 'decline', false)"
									><span
										><Icon type="declines" class="svg" colour="#ffffff" /> <span>{{ buttonDefaults.labels.decline }}</span></span
									></span
								>
							</div>
						</div>

						<p>
							Simply choose <strong class="form-default">{{ buttonDefaults.labels.decline }}</strong
							>. We'll send a nice email on your behalf without sharing your brand details.
						</p>
					</div>
				</div>

				<div class="brand-secondary-content content" v-if="!showWhatsNext || !showConnectWindow">
					<hr v-if="full" class="opening-hr" />

					<p v-if="brand.customer_gender?.label && !full">
						<span class="label">Customer Gender: </span>
						<BrandLabel :full="full" :disabled="disabled" fieldName="customer_gender" :brand="brand" :matchBrand="matchBrand" />
					</p>

					<p v-if="brand.customer_age?.label && !full">
						<span class="label">Customer Age: </span>
						<BrandLabel :full="full" :disabled="disabled" fieldName="customer_age" :brand="brand" :matchBrand="matchBrand" />
					</p>

					<p v-if="brand.service && !!brand.brand_name && !full"><span class="label">Product/Service:</span> {{ brand.service }}</p>

					<p v-if="brand.brand_attributes?.label?.length > 0 && full">
						<span class="label">Good to know: </span>
						<BrandLabel :full="full" :disabled="disabled" fieldName="brand_attributes" :brand="brand" :matchBrand="matchBrand" />
					</p>

					<hr v-if="brand.brand_attributes?.label?.length > 0 && full" />

					<p v-if="full && !isOpportunityDisplay">
						<span class="label">Interested in: </span>
						<BrandLabel :full="full" :disabled="disabled" fieldName="what_partnerships" :brand="brand" :matchBrand="matchBrand" />
					</p>

					<hr v-if="brand.pricing_relative?.label && full && !isOpportunityDisplay" />

					<p v-if="brand.pricing_relative?.label">
						<span class="label">Marketplace pricing: </span>
						<BrandLabel :full="full" :disabled="disabled" fieldName="pricing_relative" :brand="brand" :matchBrand="matchBrand" />
					</p>

					<hr v-if="brand.marketing_direction?.label && full" />

					<p v-if="brand.marketing_direction?.label && full">
						<span class="label">Marketing direction: </span>
						<BrandLabel :full="full" :disabled="disabled" fieldName="marketing_direction" :brand="brand" :matchBrand="matchBrand" />
					</p>

					<hr v-if="brand.customer_location?.label && full" />

					<p v-if="brand.customer_location?.label">
						<span class="label">Customer location: </span>
						<BrandLabel :full="full" :disabled="disabled" fieldName="customer_location" :brand="brand" :matchBrand="matchBrand" />
					</p>

					<span v-if="full">
						<div class="button-wrap center" v-if="showWhatsNext">
							<span class="button flip-colours mini hide-on-desktop" @click="toggleConnectWindow">Respond to request</span>
						</div>
					</span>
				</div>
			</div>
		</div>

		<BrandActionButtons
			:buttonDefaults="buttonDefaults"
			:brand="brand"
			:singleBrand="full"
			@action="brandAction"
			v-if="hasOptions"
			class="options" />

		<div v-if="showQuickBriefAction" :class="{ 'quick-brief-footer': true, open: showQuickBriefList, full: full }">
			<div class="quick-brief-footer__actions">
				<button type="button" class="button no-margin no-case flip-colours mini quick-brief-footer__button" @click="toggleQuickBriefList">
					{{ showQuickBriefList ? "Hide quick request brief options" : "Quick request brief" }}
				</button>
			</div>

			<QuickBriefCollabList v-if="showQuickBriefList" class="quick-brief-footer__list" @select="handleQuickBriefSelect" />
		</div>

		<div class="breakdown-wrap" v-if="breakdown && showBreakdown">
			<h2>Breakdown</h2>
			<span class="close" @click="showBreakdown = false">X</span>

			<div :class="{ breakdown: true }" v-for="(breakdownType, breakdownIndex) in breakdown" :key="'breakdown-' + breakdownIndex">
				<h3 class="breakdown-title">{{ breakdownType.label }}</h3>

				<div
					:class="{ breakdownType: true }"
					v-for="(breakdownItem, breakdownItemIndex) in breakdownType.breakdown"
					:key="'breakdown-item-' + breakdownItemIndex">
					<span class="field">{{ breakdownItem.field }}</span> <strong>{{ breakdownItem.score }}</strong>
					<span v-if="breakdownItem.maxScore === 0">deducted</span
					><span v-if="breakdownItem.maxScore > 0"
						>of possible <strong>{{ breakdownItem.maxScore }}</strong></span
					><span v-if="breakdownItem.extra" class="extra"> {{ breakdownItem.extra }}</span>
				</div>
			</div>

			<div class="breakdown">
				<h3 class="breakdown-title">Actual Score</h3>
				<div class="breakdownType">
					<strong>{{ brand.details.score }}</strong> of a possible <strong>{{ brand.details.maxScore }}</strong>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { ref, computed, watch } from "vue";
import { useVueInstance } from "../../composables/useVueInstance";
import general from "./../../utils/general";
import ApiService from "./../../services/api.service";
import BrandLabel from "./BrandLabel.vue";
import Tabs from "./Tabs.vue";
import Icon from "./BaseIcon.vue";
import BrandActionButtons from "./BrandActionButtons.vue";
import CollabTypes from "./CollabTypes.vue";
import BrandLogo from "./BrandLogo.vue";
import QuickBriefCollabList from "./QuickBriefCollabList.vue";

const loggedBrandMetaIds = new Set();

export default {
	name: "BrandCard",
	components: {
		Icon,
		BrandLabel,
		BrandActionButtons,
		CollabTypes,
		Tabs,
		BrandLogo,
		QuickBriefCollabList,
	},
	emits: [
		"tag-clicked",
		"subscribe",
		"toggleShowInfo",
		"badgeInfo",
		"reportBrand",
		"viewMessage",
		"action",
		"scrollTop",
		"tag-removed",
		"edit-tags",
		"update:showConnectWindow",
		"update:showConnectedInfo",
		"quick-brief",
	],
	props: {
		brand: {
			type: Object,
		},
		hadAction: {
			type: String,
			default: "",
		},
		buttonDefaults: {
			type: Object,
		},
		spinning: {
			type: Boolean,
			default: false,
		},
		flip: {
			type: Boolean,
			default: false,
		},
		matchBrand: {
			type: Object,
		},
		type: {
			type: String,
		},
		fields: {
			type: Object,
		},
		full: {
			type: Boolean,
			default: false,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		showBookmark: {
			type: Boolean,
			default: false,
		},
		showingBookmarked: {
			type: Boolean,
			default: false,
		},
		slider: {
			type: Boolean,
			default: false,
		},
		dateOfInterestThumb: {
			type: String,
			default: "",
		},
		dateOfInterestLabel: {
			type: String,
			default: "",
		},
		dateOfInterestItems: {
			type: Array,
			default: () => [],
		},
		currentUserDateOfInterestIds: {
			type: Array,
			default: () => [],
		},
		useHeaderTabs: {
			type: Boolean,
			default: false,
		},
		showConnectWindowOverride: {
			type: Boolean,
			default: undefined,
		},
		showConnectedInfoOverride: {
			type: Boolean,
			default: undefined,
		},
		displayMode: {
			type: String,
			default: "default",
		},
	},
	setup(props, { emit }) {
		const { store, route, $toast } = useVueInstance();

		// data
		const maxDescription = 22; // Not reactive, just a constant
		const quickBriefEnabled = false;
		const showMoreDescription = ref(false);
		const showConnectedInfo = ref(true);
		const showConnectWindow = ref(true);
		const tab = ref(0);
		const showBreakdown = ref(false);
		const showQuickBriefList = ref(false);
		const quickBriefContext = ref({
			source: "button",
			dateOfInterest: null,
		});

		// From mapState
		const collabInfo = computed(() => store.state.general.collabInfo);
		const subscription = computed(() => store.state.general.subscription);
		const showingBrandNames = computed(() => store.state.general.showingBrandNames);
		const brandInfoPublic = computed(() => store.state.general.brandInfoPublic);

		// From mapActions
		const loadCollabIdeas = () => store.dispatch("general/loadCollabIdeas");

		// Opportunity display mode
		const isOpportunityDisplay = computed(() => props.displayMode === "opportunity");

		const syncTabStateFromProps = () => {
			showConnectWindow.value = typeof props.showConnectWindowOverride === "boolean" ? props.showConnectWindowOverride : true;
			showConnectedInfo.value = typeof props.showConnectedInfoOverride === "boolean" ? props.showConnectedInfoOverride : true;
			tab.value = showConnectWindow.value ? 0 : 1;
		};

		// created hook logic
		if (props.brand.status === "connected") {
			loadCollabIdeas();
		}
		syncTabStateFromProps();

		// Methods
		const subscribe = (e) => {
			if (e) e.stopPropagation();
			emit("subscribe");
		};

		const toggleShowInfo = (e) => {
			e.stopPropagation();
			emit("toggleShowInfo");
		};

		const badgeClick = (type) => {
			emit("badgeInfo", { brandId: props.brand.brand_id, name: props.brand.brand_name, type: type });
		};

		const snoozeExpires = (d) => {
			let dd = general.timeFuture(d, false, true);

			if (baseType.value === "requests" && props.brand.connectType === "connector") {
				return `Snoozed, expires in ${dd}`;
			}

			return dd + " to respond";
		};

		const tabChange = ({ tab: newTab }) => {
			if (newTab.field === "showConnectedInfo") {
				showConnectedInfo.value = newTab.value;
				emit("update:showConnectedInfo", newTab.value);
			}
			if (newTab.field === "showConnectWindow") {
				showConnectWindow.value = newTab.value;
				tab.value = newTab.value ? 0 : 1;
				emit("update:showConnectWindow", newTab.value);
			}
		};

		const reportBrand = () => {
			emit("reportBrand", props.brand.brand_id);
		};

		const viewMessage = (messageInfo) => {
			if (limitedNoSubscription.value) {
				return subscribe();
			}
			emit("viewMessage", messageInfo);
		};

		const action = (brandId, actionType, forceIt) => {
			if (limitedNoSubscription.value && ["confirm", "snooze"].indexOf(actionType) >= 0) {
				return subscribe();
			}
			emit("action", { brandId, action: actionType, forceIt });
		};

		const viewBrand = (e, breakdownIfExists = false) => {
			if (breakdownIfExists && !!breakdown.value) {
				if (e) e.stopPropagation();
				showBreakdown.value = true;
				return;
			}
			if (viewBrandAllowed.value) {
				action(props.brand.brand_id, "show");
			}
		};

		const openTopSnippet = (e) => {
			if (e) e.stopPropagation();
			const snippet = topBrandMetaSnippet.value;
			if (!snippet || snippet.key !== "active_pitch") return;

			let pitchId = Number(snippet.pitchId);

			if (!Number.isFinite(pitchId) || pitchId <= 0) {
				const url = typeof snippet.url === "string" ? snippet.url : "";
				const match = url.match(/\/(?:opportunities|pitch)\/(\d+)/);
				if (match && match[1]) {
					pitchId = Number(match[1]);
				}
			}

			if (Number.isFinite(pitchId) && pitchId > 0) {
				store.commit("general/closeBrandCardPopup");
				store.commit("general/openPitchBrandPopup", {
					pitchId,
					source: "brands",
				});
			}
		};

		const brandAction = ({ brandId, action: actionType, forceIt }) => {
			if (actionType === "show" || actionType === "view" || actionType === "view-respond") {
				viewBrand();
				return;
			}
			action(brandId, actionType, forceIt);
		};

		const toggleConnectWindow = () => {
			showConnectWindow.value = !showConnectWindow.value;
			tab.value = showConnectWindow.value ? 0 : 1;
			emit("update:showConnectWindow", showConnectWindow.value);
			emit("scrollTop");
		};

		const toggleQuickBriefList = (event) => {
			if (event) event.stopPropagation();
			quickBriefContext.value = {
				source: "button",
				dateOfInterest: null,
			};
			showQuickBriefList.value = !showQuickBriefList.value;
		};

		const openQuickBriefList = (event, context = {}) => {
			if (event) event.stopPropagation();
			quickBriefContext.value = {
				source: context.source || "button",
				dateOfInterest: context.dateOfInterest || null,
			};
			showQuickBriefList.value = true;
		};

		const onDateOfInterestQuickBrief = (dateInfo, event) => {
			openQuickBriefList(event, {
				source: "date-of-interest",
				dateOfInterest: dateInfo,
			});
		};

		const handleQuickBriefSelect = (item) => {
			emit("quick-brief", {
				brandId: props.brand.brand_id,
				brand: props.brand,
				collabType: item,
				context: quickBriefContext.value,
			});
		};

		const handleMoreHelp = (event) => {
			if (event) event.stopPropagation();
			store.commit("general/openCollabHelpPopup", {
				source: "brand-card-connected-info",
				brandId: props.brand.brand_id,
			});
		};

		// Computed
		const isActive = computed(() => {
			return !!subscription.value && !!subscription.value.active;
		});

		const isApproved = computed(() => {
			return !!subscription.value && !!subscription.value.approved;
		});

		const breakdown = computed(() => {
			if (!props.brand.details || !props.fields || !Object.keys(props.fields).length) return null;

			let breakdown = [],
				d = props.brand.details;

			function capitalizeFirstLetter(string) {
				return string.charAt(0).toUpperCase() + string.slice(1);
			}

			["success", "fail"].forEach((type) => {
				if (d[type] && Object.keys(d[type]).length > 0) {
					let t = {
						label: capitalizeFirstLetter(type),
						breakdown: [],
					};

					Object.keys(d[type]).forEach((field) => {
						const fieldMeta = props.fields[field];
						const info = d[type][field];

						if (!fieldMeta || !info || typeof info !== "object") return;

						let r = {
							field: fieldMeta.label,
							score: info.score,
							maxScore: info.maxScore,
							extra: "",
						};

						if (info.match) {
							if (typeof info.match === "string") {
								r.extra = " (" + info.match + ")";
							}

							if (Array.isArray(info.match)) {
							} else if (info.match.matches) {
								if (info.match.matchesLabels) {
									r.extra = " ( " + info.match.matchesLabels.join(", ") + " )";
								} else {
									r.extra = " (" + info.match.matches.length + " match" + (info.match.matches.length !== 1 ? "es" : "") + ")";
								}
							}
						}

						t.breakdown.push(r);
					});

					if (t.breakdown.length > 0) {
						breakdown.push(t);
					}
				}
			});
			return breakdown;
		});

		const matchLevel = computed(() => {
			return !!breakdown.value && !!props.brand.details.matchLevel && general.isLocal() ? props.brand.details.matchLevel : "";
		});

		const isNew = computed(() => {
			return props.brand.new && !props.full && normalizedType.value !== "declines";
		});

		const hideBrandName = computed(() => {
			return !!props.brand.hideBrandName;
		});

		const hideBrandNameMessage = computed(() => {
			return "The brands name is hidden, please subscribe to view and respond";
		});

		const displayBrandName = computed(() => {
			let label = !!props.brand.brand_name && !hideBrandName.value ? props.brand.brand_name : props.brand.service;
			if (!props.brand.brand_name && !props.full) {
				let l = label.split(" "),
					wordCount = 10,
					dots = false;
				if (l.length > wordCount) {
					l = l.slice(0, wordCount);
					dots = true;
				}
				label = l.join(" ") + (dots ? "..." : "");
			}
			return {
				label: label,
			};
		});

		const showingActualBrandName = computed(() => {
			if (props.brand.status === "connected") return true;
			if (props.brand.status === "requested" && props.brand.requestType === "received") {
				return !hideBrandName.value;
			}
			return !!props.brand.brand_name && !hideBrandName.value;
		});

		const viewBrandAllowed = computed(() => {
			return ["requests", "connections", "matches"].indexOf(props.type) >= 0 || props.brand.status === "uninterested";
		});

		const baseType = computed(() => {
			let s = props.brand && props.brand.status ? props.brand.status : null;
			if (props.type === "declines" && s === "uninterested") return "uninterested";
			if (props.type) return props.type;
			return s;
		});

		const normalizedType = computed(() => {
			let t = baseType.value;
			switch (t) {
				case "requested":
					return "requests";
				case "connected":
					return "connections";
				case "declined":
					return "declines";
			}
			return t;
		});

		const isSnoozed = computed(() => {
			return props.brand.snoozed && props.brand.snoozed !== "0";
		});

		const expires = computed(() => {
			if (!props.brand.requestExpires) return null;
			let d = new Date(props.brand.requestExpires);
			if (d.getTime() > new Date().getTime()) {
				return {
					remaining: snoozeExpires(d),
					remainingDays: general.timeFuture(d, false, true),
				};
			}
			return null;
		});

		const tabs = computed(() => {
			if (props.useHeaderTabs) return null;
			if (!props.full) return null;
			let tabs = [],
				field = isConnected.value ? "showConnectedInfo" : "showConnectWindow";
			if (isConnected.value) {
				tabs.push({
					label: "Contact " + props.brand.brand_name,
					value: true,
					field: field,
				});
			}
			if (showWhatsNext.value) {
				tabs.push({
					label: "Connecting with " + (props.brand.brand_name || "this brand"),
					value: true,
					field: field,
				});
			}
			if (tabs.length === 0) return null;
			tabs.push({
				label: "All about " + (props.brand.brand_name || "this brand"),
				value: false,
				field: field,
			});
			return tabs;
		});

		const tabbed = computed(() => {
			return props.full && (isConnected.value || showWhatsNext.value);
		});

		const showWhatsNext = computed(() => {
			return props.brand.status === "requested" && props.brand.requestType !== "sent" && props.full;
		});

		const limitedNoSubscription = computed(() => {
			return !!props.brand.limitedNoSubscription;
		});

		const showingConnectedInfo = computed(() => {
			return props.full && !!showConnectedInfo.value && props.brand.status === "connected";
		});

		const isConnected = computed(() => {
			return props.brand.status === "connected";
		});

		const isReceiver = computed(() => {
			return props.matchBrand.id !== props.brand.connector;
		});

		const shouldShowReportBrand = computed(() => {
			if (!props.full) return false;
			if (route.name === "requests") return isReceiver.value;
			return ["connections", "declines"].indexOf(route.name) > -1;
		});

		const brandDescription = computed(() => {
			if (!props.full) return "";
			if (props.brand && props.brand.brand_description) {
				let d = props.brand.brand_description.split(" ");
				if (!showMoreDescription.value) {
					d = d.slice(0, maxDescription);
					if (d[d.length - 1].substring(d.length - 1) === ".") {
						d[d.length - 1] = d[d.length - 1].substring(0, d[d.length - 1].length - 1);
					}
				}
				return d.join(" ");
			}
			return "";
		});

		const hasMoreDescription = computed(() => {
			if (props.brand && props.brand.brand_description) {
				return props.brand.brand_description.split(" ").length > maxDescription;
			}
			return false;
		});

		const topLabel = computed(() => {
			let label = "",
				colour = "#FFA500",
				doDateTime = true,
				icon = "";

			switch (props.type) {
				case "requests":
					if (props.matchBrand.id === props.brand.connector) {
						label = "Request sent ";
						//icon = "sent";
					} else {
						label = "Request received ";
						//icon = "received";
					}
					break;
				case "connections":
					label = "You matched ";
					break;
				case "declines":
					if (props.brand.status === "uninterested") {
						label = "You weren't interested ";
					} else if (props.brand.status.split("-")[0] === "declined" && props.brand.connectType === "connectee") {
						label = "You declined ";
					} else {
						label = "They declined ";
					}
					break;
			}

			if (label) {
				return {
					label: label + (doDateTime ? general.timeAgo(props.brand.connectionUpdated, "jS M Y", "H:i", "jS M") : ""),
					colour: colour,
					icon: icon,
				};
			}
			return null;
		});

		const connectedDateLabel = computed(() => {
			if (props.brand.status !== "connected" || !props.brand.connectionUpdated) return "";

			return `Connected ${general.dateFormat("jS M Y", props.brand.connectionUpdated)}`;
		});

		const extractHandleFromUrl = (value, domains = []) => {
			if (typeof value !== "string") return "";

			const trimmedValue = value.trim();
			if (!trimmedValue) return "";

			const normalizedValue = /^https?:\/\//i.test(trimmedValue) ? trimmedValue : `https://${trimmedValue.replace(/^\/+/, "")}`;

			try {
				const parsedUrl = new URL(normalizedValue);
				const hostname = parsedUrl.hostname.toLowerCase();

				if (domains.length > 0 && !domains.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`))) {
					return "";
				}

				return (
					parsedUrl.pathname
						.split("/")
						.map((part) => part.trim())
						.filter(Boolean)[0] || ""
				);
			} catch (error) {
				return "";
			}
		};

		const sanitizeSocialHandle = (value, domains = []) => {
			if (typeof value !== "string") return "";

			const trimmedValue = value.trim();
			if (!trimmedValue) return "";

			const handleFromUrl = extractHandleFromUrl(trimmedValue, domains);
			const rawHandle = handleFromUrl || trimmedValue;

			return rawHandle
				.replace(/^@+/, "")
				.replace(/^\/+|\/+$/g, "")
				.split("?")[0]
				.split("#")[0]
				.trim();
		};

		const getSocialFieldValue = (value) => {
			if (typeof value === "string") return value;
			if (!value || typeof value !== "object") return "";

			if (typeof value.value === "string") return value.value;
			if (Array.isArray(value.value)) {
				return value.value.find((entry) => typeof entry === "string" && entry.trim().length > 0) || "";
			}

			return "";
		};

		const getFacebookUrl = () => {
			const facebookValue =
				getSocialFieldValue(props.brand.facebook_url) ||
				getSocialFieldValue(props.brand.facebookUrl) ||
				getSocialFieldValue(props.brand.facebook) ||
				getSocialFieldValue(props.brand.facebook_page) ||
				getSocialFieldValue(props.brand.facebookPage) ||
				"";

			if (typeof facebookValue !== "string") return "";

			const trimmedValue = facebookValue.trim();
			if (!trimmedValue) return "";

			if (/^https?:\/\//i.test(trimmedValue)) return trimmedValue;
			if (/^facebook\.com\//i.test(trimmedValue) || /^www\.facebook\.com\//i.test(trimmedValue)) return `https://${trimmedValue}`;

			const pageHandle = sanitizeSocialHandle(trimmedValue);
			return pageHandle ? `https://www.facebook.com/${pageHandle}` : "";
		};

		const getInstagramUrl = () => {
			const handle = sanitizeSocialHandle(getSocialFieldValue(props.brand.instagram), ["instagram.com"]);
			return handle ? `https://www.instagram.com/${handle}/` : "";
		};

		const getTikTokUrl = () => {
			const handle = sanitizeSocialHandle(getSocialFieldValue(props.brand.tiktok), ["tiktok.com"]);
			return handle ? `https://www.tiktok.com/@${handle}` : "";
		};

		const socials = computed(() => {
			return [
				{
					icon: "instagram",
					vars: "instagram_followers",
					label: "Followers",
					href: getInstagramUrl(),
				},
				{
					icon: "email",
					vars: "email_database",
					label: "Subscribers",
					href: "",
				},
				{
					icon: "facebook",
					vars: "facebook_followers",
					label: "Followers",
					href: getFacebookUrl(),
				},
				{
					icon: "tiktok",
					vars: "tiktok_followers",
					label: "Followers",
					href: getTikTokUrl(),
				},
			];
		});

		const normalizedLogoBgColour = computed(() => {
			const value = props.brand?.brand_logo_bg_colour;
			if (typeof value !== "string") return "";
			const colour = value.trim();
			return /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(colour) ? colour : "";
		});

		const logoContainerStyles = computed(() => {
			return normalizedLogoBgColour.value ? { backgroundColor: normalizedLogoBgColour.value } : {};
		});

		const brandImg = computed(() => {
			if (props.brand.brand_logo) {
				return { img: props.brand.brand_logo, isLogo: true, white: props.brand.brand_logo_is_white };
			}
			let img;
			try {
				img = require("../../assets/img/brands/" + props.brand.brand_category.value[0] + ".jpg");
			} catch (err) {
				img = require("../../assets/img/brands/0.jpg");
			}
			return { img: img, isLogo: false };
		});
		const id = computed(() => {
			return props.matchBrand.brand_id + "-" + props.brand.brand_id;
		});
		const hasOptions = computed(() => {
			return (
				(baseType.value === "matches" ||
					baseType.value === "requests" ||
					baseType.value === "uninterested" ||
					(baseType.value === "declines" && props.brand.connectType === "connectee")) &&
				!props.disabled &&
				!props.full &&
				!props.brand.justHappened
			);
		});

		const topSnippetPanelExternalLink = computed(() => {
			const snippet = topBrandMetaSnippet.value;
			if (!snippet) return "";
			if (topSnippetIsCollaborationHistory.value) return snippet.externalLink || "";
			return "";
		});

		const brandMetaSnippets = computed(() => {
			if (!Array.isArray(props.brand?.brand_meta?.snippets)) return [];
			return props.brand.brand_meta.snippets;
		});

		const topBrandMetaSnippet = computed(() => {
			if (brandMetaSnippets.value.length === 0) return null;
			return brandMetaSnippets.value[0];
		});

		const topSnippetIsActivePitch = computed(() => {
			return topBrandMetaSnippet.value?.key === "active_pitch";
		});

		const topSnippetIsCollaborationHistory = computed(() => {
			return topBrandMetaSnippet.value?.key === "collab_history";
		});

		const topSnippetPanelTitle = computed(() => {
			const snippet = topBrandMetaSnippet.value;
			if (!snippet) return "";
			if (topSnippetIsActivePitch.value && snippet.pitchTitle) return snippet.pitchTitle;
			if (topSnippetIsCollaborationHistory.value && snippet.collabTitle) return snippet.collabTitle;
		});

		const topSnippetPanelValue = computed(() => {
			const snippet = topBrandMetaSnippet.value;
			if (!snippet) return "";
			if (topSnippetIsActivePitch.value) return snippet.value || "";
			return snippet.value || "";
		});

		const topSnippetPanelImageUrl = computed(() => {
			const snippet = topBrandMetaSnippet.value;
			return !!snippet && typeof snippet.imageUrl === "string" ? snippet.imageUrl : "";
		});

		const topSnippetPanelLabel = computed(() => {
			const snippet = topBrandMetaSnippet.value;
			if (!snippet) return "";
			return snippet.label || "";
		});

		const topSnippetPanelKey = computed(() => {
			const snippet = topBrandMetaSnippet.value;
			if (!snippet) return "";
			return snippet.key || "dummy";
		});

		const topSnippetIsClickable = computed(() => {
			if (topSnippetIsCollaborationHistory.value) {
				return typeof topSnippetPanelExternalLink.value === "string" && topSnippetPanelExternalLink.value.length > 0;
			}
			const snippet = topBrandMetaSnippet.value;
			if (!snippet || !topSnippetIsActivePitch.value) return false;
			const pitchId = Number(snippet.pitchId);
			if (Number.isFinite(pitchId) && pitchId > 0) return true;
			return typeof snippet.url === "string" && snippet.url.length > 0;
		});

		const normalizeDateOfInterestId = (value) => {
			const id = Number(value);
			return Number.isInteger(id) && id > 0 ? id : null;
		};

		const currentUserDateOfInterestIdSet = computed(() => {
			const ids = Array.isArray(props.currentUserDateOfInterestIds) ? props.currentUserDateOfInterestIds : [];
			return new Set(ids.map((value) => normalizeDateOfInterestId(value)).filter(Boolean));
		});

		const dateOfInterestItemsProcessed = computed(() => {
			if (!Array.isArray(props.dateOfInterestItems)) return [];
			return props.dateOfInterestItems
				.map((item) => {
					const id = normalizeDateOfInterestId(item?.id);
					if (!item || !id || !item.label || !item.thumb) return null;
					return {
						...item,
						id,
						isCurrentUserMatch: currentUserDateOfInterestIdSet.value.has(id),
						tooltipLabel: currentUserDateOfInterestIdSet.value.has(id) ? `You’re both interested in ${item.label}` : item.label,
					};
				})
				.filter(Boolean);
		});

		const showDatesOfInterestFallbackPanel = computed(() => {
			return !topBrandMetaSnippet.value && dateOfInterestItemsProcessed.value.length > 0;
		});

		const showTopSignalPanel = computed(() => {
			return !!topBrandMetaSnippet.value;
		});

		const showBrandMetaPanel = computed(
			() => (showTopSignalPanel.value || dateOfInterestItemsProcessed.value.length > 0) && props.brand.status === "potential"
		);

		const showQuickBriefAction = computed(() => {
			return quickBriefEnabled && props.brand.status === "potential";
		});

		const removeTag = async (tagId) => {
			try {
				await ApiService.delete(`brand/bookmark/${props.matchBrand.brand_id}/${props.brand.brand_id}/tags/${tagId}`);
				emit("tag-removed", { brandId: props.brand.brand_id, tagId });
				$toast.success("Tag Removed: Tag removed from saved brand");
			} catch (error) {
				$toast.error("Error: Failed to remove tag");
			}
		};

		const editTags = () => {
			emit("edit-tags", {
				brandId: props.brand.brand_id,
				userBrandId: props.matchBrand.brand_id,
				existingTags: props.brand.tags ? props.brand.tags.map((t) => t.id) : [],
				brandName: props.brand.brand_name || "",
			});
		};

		watch(
			() => props.brand,
			(brand) => {
				if (!brand || !brand.brand_id) return;
				if (loggedBrandMetaIds.has(brand.brand_id)) return;

				const hasBrandMeta = !!brand.brand_meta && (Array.isArray(brand.brand_meta.snippets) || typeof brand.brand_meta.metrics === "object");
				const hasSignals =
					typeof brand.signal_active_pitch_count !== "undefined" ||
					typeof brand.popular_requests_received_30d !== "undefined" ||
					typeof brand.signal_collab_examples_count !== "undefined";
				const snippets = Array.isArray(brand.brand_meta?.snippets) ? brand.brand_meta.snippets : [];
				const signalValues = {
					popular30d: Number(brand.popular_requests_received_30d) || 0,
					activePitchCount: Number(brand.signal_active_pitch_count) || 0,
					collabExamplesCount: Number(brand.signal_collab_examples_count) || 0,
				};
				const hasPositiveSignals = Object.values(signalValues).some((value) => value > 0);

				if (!hasBrandMeta && !hasSignals && !brand.top) return;
				if (!brand.top && snippets.length === 0 && !hasPositiveSignals) return;

				loggedBrandMetaIds.add(brand.brand_id);
			},
			{ immediate: true }
		);

		watch(
			() => props.showConnectWindowOverride,
			(value) => {
				if (typeof value !== "boolean") return;
				showConnectWindow.value = value;
				tab.value = value ? 0 : 1;
			}
		);

		watch(
			() => props.showConnectedInfoOverride,
			(value) => {
				if (typeof value !== "boolean") return;
				showConnectedInfo.value = value;
			}
		);

		watch(
			() => props.brand?.brand_id,
			() => {
				syncTabStateFromProps();
				showQuickBriefList.value = false;
				quickBriefContext.value = {
					source: "button",
					dateOfInterest: null,
				};
				if (props.brand?.status === "connected") {
					loadCollabIdeas();
				}
			}
		);

		watch(
			() => props.brand?.status,
			(status) => {
				if (status === "connected") {
					showConnectedInfo.value = typeof props.showConnectedInfoOverride === "boolean" ? props.showConnectedInfoOverride : true;
					emit("update:showConnectedInfo", showConnectedInfo.value);
					loadCollabIdeas();
				}
			}
		);

		return {
			showMoreDescription,
			showConnectWindow,
			tab,
			showBreakdown,
			showQuickBriefList,
			quickBriefContext,
			collabInfo,
			subscription,
			showingBrandNames,
			brandInfoPublic,
			loadCollabIdeas,
			toggleShowInfo,
			subscribe,
			badgeClick,
			snoozeExpires,
			tabChange,
			reportBrand,
			viewMessage,
			brandAction,
			action,
			viewBrand,
			toggleConnectWindow,
			toggleQuickBriefList,
			openQuickBriefList,
			onDateOfInterestQuickBrief,
			handleQuickBriefSelect,
			handleMoreHelp,
			isActive,
			matchLevel,
			breakdown,
			isNew,
			hideBrandName,
			hideBrandNameMessage,
			displayBrandName,
			showingActualBrandName,
			viewBrandAllowed,
			baseType,
			normalizedType,
			isSnoozed,
			expires,
			isOpportunityDisplay,
			tabs,
			tabbed,
			showWhatsNext,
			limitedNoSubscription,
			showingConnectedInfo,
			isConnected,
			shouldShowReportBrand,
			isReceiver,
			brandDescription,
			hasMoreDescription,
			topLabel,
			connectedDateLabel,
			socials,
			brandImg,
			logoContainerStyles,
			id,
			hasOptions,
			showTopSignalPanel,
			topBrandMetaSnippet,
			topSnippetPanelTitle,
			topSnippetPanelValue,
			topSnippetPanelImageUrl,
			topSnippetPanelExternalLink,
			topSnippetIsCollaborationHistory,
			topSnippetIsActivePitch,
			topSnippetIsClickable,
			topSnippetPanelLabel,
			topSnippetPanelKey,
			dateOfInterestItemsProcessed,
			openTopSnippet,
			removeTag,
			editTags,
			showBrandMetaPanel,
			showQuickBriefAction,
			isApproved,
		};
	},
};
</script>

<style lang="less" scoped>
@import "./../../assets/css/less/_settings.less";
@import "./../../assets/css/less/_helper.less";

.breakdown-wrap {
	margin: 0;
	padding: 20px;
	position: relative;

	.field {
		font-style: italic;
		display: block;
		margin: 15px 0 5px;
		font-weight: @bold;
		color: @formDefaultColour;
	}

	.close {
		position: absolute;
		right: 10px;
		top: 10px;
		cursor: pointer;
		padding: 10px 15px;
		.rounded(@radiusCircle);
		&:hover {
			background-color: @formDefaultColour;
			color: white;
		}
	}

	h3,
	h4 {
		margin: 0;
	}

	h4 {
		margin: 15px 0 0;
	}

	.breakdown-title {
		margin-top: 15px;
	}

	.breakdownType {
		margin: 0 0 5px;
		font-size: 14px;

		strong {
			font-weight: @bold;
		}

		span {
			font-size: inherit;
		}

		.extra {
			color: @guidedRose;
		}
	}

	.max {
		font-size: 14px;
		margin-top: 20px;
	}
}

.tab-wrap {
	margin: 0 0 20px;
}

.expires {
	width: 55px;
	position: absolute;
	right: 10px;
	top: 49px;
	z-index: 1;
	box-sizing: border-box;

	span {
		font-size: 10px;
		line-height: 1.2;
		display: block;
		margin-top: 5px;
		text-align: center;
		font-weight: @bold;
	}

	.svg {
		width: 40px;
		margin-left: 7px;
	}

	&:not(.snoozed) {
		border: 1px solid @formDefaultColour;
		height: 55px;
		border-radius: @radiusCircle;
		padding-top: 10px;
		right: 10px;
	}
}

.collab-types-wrap {
	margin: 32px 0 0;
	padding: 24px 0 0;
	border-top: 1px solid @borderRoseSubtle;

	.connected-brand-summary__eyebrow {
		margin-bottom: 28px !important;
	}

	p {
		color: @formDefaultColour;
		margin: 0 0 25px;
	}
	.more-help {
		margin: 20px 0 0;
		span {
			cursor: pointer;
			color: @brown;
			text-decoration: underline;
		}
	}
}
.connect-message-wrap {
	margin: 15px 0;
}
.vegan-wrap {
	margin-top: 10px;
	display: block;
}
.more-info-moble {
	color: @guidedRose;
	position: absolute;
	top: 5px;
	right: 5px;
	font-weight: @bold;
	text-decoration: underline;
	display: none;
}
.report {
	display: inline-block;
	margin-top: 35px;
	color: @guidedRose;
	cursor: pointer;
	font-style: italic;
	font-size: 12px;
	width: 70%;
	text-decoration: underline;
	&:hover {
		text-decoration: none;
	}
}
.toggle-connect-info {
	display: block;
	position: absolute;
	border-width: 2px;
	top: 0;
	left: 0px;
	z-index: 3;
	margin: 0;
	span {
		font-weight: @bold;
		color: inherit;
	}
}
.brand {
	position: relative;

	&.match-level-1 {
		&:after {
			content: "";
			position: absolute;
			top: 10px;
			left: 10px;
			width: 10px;
			height: 10px;
			background-color: #00ff00;
			border-radius: @radiusCircle;
		}
	}

	&.match-level-2 {
		&:after {
			content: "";
			position: absolute;
			top: 10px;
			left: 10px;
			width: 10px;
			height: 10px;
			background-color: #ffa500;
			border-radius: @radiusCircle;
		}
	}

	&.match-level-3 {
		&:after {
			content: "";
			position: absolute;
			top: 10px;
			left: 10px;
			width: 10px;
			height: 10px;
			background-color: #ff0000;
			border-radius: @radiusCircle;
		}
	}

	.brand-layout {
		position: relative;

		.brand-layout__sections {
			display: flex;
			flex-direction: column;
			padding-top: 20px;
		}
	}

	.brand-primary-content,
	.brand-secondary-content {
		min-width: 0;
	}

	.connected-info {
		.connected-brand-summary {
			&__eyebrow {
				margin: 0 0 14px;
			}

			&__panel {
				background: @surfacePanel;
				padding: 14px;
				.rounded(@radiusXl);
			}

			.brand-top {
				padding: 0;
				margin: 0;
				gap: 14px;
			}

			.img-wrap-outer {
				width: 116px;
				background: @surfacePanel;
				border-radius: @radiusXl;
			}

			.brand-top-info {
				font-size: 24px;
			}
		}

		.connect-with-info {
			position: relative;
			border: 1px solid fade(@guidedRose, 8%);
			background: @guidedRoseBgSubtle;
			padding: 20px 22px 18px;
			.rounded(@radiusLg);
			font-size: 18px;
			line-height: 1.45;
			color: fade(@guidedRose, 82%);
			margin: 0;
			align-self: stretch;

			&__label {
				margin: 0 0 12px;
				font-size: 11px;
				font-weight: @bold;
				letter-spacing: 0.12em;
				text-transform: uppercase;
			}

			&__name {
				font-size: 24px;
				line-height: 1.15;
				margin: 0 0 14px;
				color: @guidedRose;
			}

			&__email {
				margin: 0 0 18px;
				font-size: 19px;
				line-height: 1.4;
				color: @guidedRose;
			}

			&__note {
				font-size: 14px;
				line-height: 1.65;
				margin: 0;
				font-weight: @bold;
			}

			span,
			strong,
			a {
				color: inherit;
			}

			a {
				text-decoration: none;
				font-weight: @bold;
				word-wrap: break-word;
				border-bottom: 1px solid @guidedRoseBorder;
				padding-bottom: 1px;
				color: @guidedRose;
				&:hover {
					border-bottom-color: @guidedRose;
				}
			}
		}
	}

	.new {
		position: absolute;
		top: 20px;
    	left: -25px;
		background-color: fade(#fff, 85%);
		border: 1px solid @borderRoseStrong;
		color: @brown;
		padding: 0;
		line-height: 1.4em;
		width: 110px;
		text-align: center;
		z-index: 1;
		font-size: 12px;
		font-weight: @bold;
		letter-spacing: 0.5px;
		transform: rotate(-45deg);
		transform-origin: center center;
		box-shadow: @elevationEditorialBadgeShadowSubtle;
		&:before {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			background-color: @guidedRoseBgStrong;
			content: "";
		}
	}

	.category-label {
		position: absolute;
		top: 5px;
		right: 5px;
		border: 1px solid @borderRoseStrong;
		color: @textSubtle;
		padding: 0 6px;
		z-index: 1;
		font-size: 12px;
		border-radius: @radiusXs;
	}

	&.full {
		&:not(.tabbed) {
			.category-label {
				right: 40px;
				top: 10px;
			}
		}
	}

	&:not(.empty) {
		padding: 0 0 20px 0;
		background: @surfacePanel;
		.elevation.card();
		.rounded(4px);
		margin: 0 0 20px 0;
		overflow: hidden;
		&.connected {
			margin: 0;
			padding: 0;
		}
	}

	.vegan-icon {
		width: 18px;
		height: 18px;
		margin-right: 8px;
	}

	&.declined {
		filter: grayscale(100%);
		.trans;
	}

	.corner-label {
		position: absolute;
		display: block;
		left: 0;
		top: 0;
		background-color: @guidedRose;
		color: #fff;
		padding: 2px 5px;
		font-size: 12px;
	}

	.hover-info {
		position: absolute;
		right: 5px;
		top: 3px;
		font-size: 12px;
		.trans;
		color: @formDefaultColour;
	}

	.top-label {
		position: absolute;
		display: block;
		right: 0;
		top: 0;
		color: @guidedRose;
		padding: 6px 20px 0;
		font-size: 12px;
		width: 100%;
		text-align: left;

		.svg {
			width: 20px;
			margin: 2px 5px 2px 0;
		}
	}

	.percent {
		position: absolute;
		top: 0;
		left: 10px;
	}

	&.had-action {
		position: relative;
	}

	.action {
		width: 100%;
		height: 100%;
		position: absolute;
		top: 0;
		left: 0;
		z-index: 3;
		right: 0;
		bottom: 0;
		background: rgba(255, 255, 255, 0.9);
		display: none;

		&.show {
			display: block;
		}

		.action-inner {
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			z-index: 4;

			p {
				margin: 0;
				padding: 0;
				text-align: center;
				color: @formDefaultColour;
				font-size: 30px;
			}
		}
	}

	&.hover-over {
		cursor: pointer;

		&:hover {
			.action {
				display: block;
			}
		}
	}

	.img-wrap-outer {
		position: relative;

		&.clickable {
			cursor: pointer;
		}
		.label {
			position: absolute;
			top: 50%;
			transform: translate(-50%, -50%);
			left: 50%;
			width: 80%;
			text-align: center;
			font-size: 14px;
			color: @darkContrast;
			background-color: rgba(255, 255, 255, 0.65);
			padding: 5px;
			.rounded(@radiusMd);
			margin: 0;
			box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
			cursor: default;
			.trans;

			&.clickable {
				cursor: pointer;
				&:hover {
					box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.3);
					background-color: rgba(255, 255, 255, 0.85);
				}
			}
		}
	}

	.brand-logo {
		margin: 0 auto 30px;
		width: 54%;
		&:not(.is-logo) {
			margin-top: 5px;
		}
	}

	.brand-top {
		display: flex;
		align-items: center;
		gap: 26px;
		padding: 0 20px;
		margin-bottom: 14px;

		.img-wrap-outer {
			background: #fff;
			border-radius: @radiusXl;
			.elevation.card();
			flex-shrink: 0;
			width: 150px;
			padding: 12px;
			box-sizing: border-box;
		}

		.brand-logo {
			width: 100%;
			margin: 0;
		}

		.brand-top-info {
			flex: 1;
			min-width: 0;
			font-size: 26px;

			&.is-masked-name {
				font-size: 22px;
			}

			.connected-date {
				margin: 5px 0 0;
				color: @guidedRose;
				font-size: 13px;
				font-weight: 400;
				line-height: 1.25;
			}

			.name {
				text-align: left;
				line-height: 1.3;
				margin: 0;
				color: @darkContrast;
				font-size: inherit;

				&.standard {
					font-weight: 400;
				}

				.req-from-text {
					font-size: 0.4em;
					display: inline-block;
					color: @guidedRose;
				}

				a {
					color: inherit;
					text-decoration: none;

					&:hover {
						text-decoration: none;
						strong {
							text-decoration: underline;
						}
					}

					&.disabled {
						cursor: default;

						&:hover {
							text-decoration: none;
						}
					}
				}
			}

			.brand-head-badge {
				display: flex;
				align-items: center;
				gap: 10px;
				margin-top: 10px;
				color: @textSubtle;

				.svg {
					width: 19px;
					height: 19px;
					flex-shrink: 0;
				}

				a,
				span {
					color: inherit;
					font-size: 0.65em;
					line-height: 1.2;
					text-decoration: underline;
					text-decoration-thickness: 1px;
					text-underline-offset: 2px;
					word-break: break-word;
				}

				span {
					text-decoration: none;
					font-size: 0.55em;
				}
				&.clickable {
					span {
						cursor: pointer;
						&:hover {
							text-decoration: underline;
						}
					}
				}
			}
			.brand-head-badges {
				display: flex;
				flex-wrap: wrap;
				margin-top: 5px;
				.brand-head-badge {
					gap: 5px;
					&:after {
						content: " ";
						margin: 0 5px 0 0;
						color: @textSubtle;
					}
					&:last-child {
						&:after {
							content: none;
						}
					}
				}
			}
		}
	}

	.brand-meta-panel {
		margin: 0 20px 14px;
		padding: 6px 6px 6px 14px;
		border: 1px solid @guidedRoseBorder;
		border-radius: @radiusXl;
		position: relative;
		overflow: hidden;

		&.testimonial {
			padding-top: 24px;
		}

		&.doi {
			overflow: visible;
			border: none;
			padding: 0;
		}

		&__label {
			display: inline-block;
			position: absolute;
			top: 0;
			left: 0;
			padding: 1px 10px;
			border-radius: 0 0 4px 0;
			font-size: 11px;
			color: white;
			letter-spacing: 0.5px;
			margin-bottom: 8px;
			background-color: @formDefaultColour;
		}

		&__title {
			margin: 14px 0 0;
			font-size: 16px;
			line-height: 1.3;
			color: @darkContrast;
		}

		&__value {
			margin: 0;
			font-size: 14px;
			line-height: 1.4;
			color: @textBody;
		}

		&__active-pitch {
			display: flex;
			gap: 14px;
			align-items: center;
			padding: 2px 0;

			&.clickable {
				cursor: pointer;
			}
		}

		&__active-pitch-copy {
			flex: 1;
			min-width: 0;
		}

		&__cta {
			display: inline-block;
			margin-top: 0;
			font-size: 12px;
			font-weight: @bold;
			color: @formDefaultColour;
			text-decoration: underline;
		}

		&__active-pitch-image {
			width: 60px;
			height: 60px;
			flex-shrink: 0;
			border-radius: @radiusLg;
			overflow: hidden;
			background: #fff;
			.elevation.card();

			img {
				display: block;
				width: 100%;
				height: 100%;
				object-fit: cover;
			}
		}

		&__doi-title {
			margin: 0;
			font-size: 14px;
			font-weight: @bold;
			color: @darkContrast;
			line-height: 1.3;

			&--inline {
				flex-shrink: 0;
				color: @formDefaultColour;
			}
		}

		&__doi-mutual-wrap {
			display: inline-flex;
			flex-direction: row;
			flex-wrap: wrap;
			align-items: center;
			column-gap: 14px;
			row-gap: 8px;
			min-width: 0;
		}

		&__doi-split-stack {
			display: flex;
			flex-direction: column;
			align-items: stretch;
			gap: 10px;
			width: 100%;
		}

		&__doi-column {
			display: flex;
			flex-direction: column;
			align-items: flex-start;
			gap: 10px;
			min-width: 0;
		}

		&__doi-columns {
			display: inline-flex;
			flex-direction: row;
			flex-wrap: wrap;
			align-items: flex-start;
			justify-content: flex-start;
			column-gap: 14px;
			row-gap: 16px;
			max-width: 100%;
		}

		.date-interest-row {
			display: flex;
			align-items: center;
			padding: 2px 0;
			flex: 0 0 auto;
		}

		.date-interest-row__item {
			position: relative;
			width: 36px;
			height: 36px;
			margin-left: -10px;
			z-index: 1;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			outline: none;
			cursor: pointer;

			&:first-child {
				margin-left: 0;
			}

			&:hover,
			&:focus,
			&:focus-within {
				z-index: 6;
			}

			.date-interest-thumb__img {
				width: 36px;
				height: 36px;
				border-radius: @radiusCircle;
				overflow: hidden;
				background: #fff;
				box-sizing: border-box;
				display: inline-flex;
				align-items: center;
				justify-content: center;
				.elevation.card();
				transition:
					border-color 0.2s ease,
					box-shadow 0.2s ease,
					transform 0.2s ease,
					outline-color 0.2s ease;
			}

			&.is-current-user-match {
				z-index: 2;

				.date-interest-thumb__img {
					border: 2px solid @formDefaultColour;
					box-shadow: 0 3px 10px fade(@formDefaultColour, 30%);
				}
			}

			img {
				display: block;
				width: 100%;
				height: 100%;
				object-fit: cover;
			}

			.date-interest-tooltip {
				position: absolute;
				left: 0;
				top: calc(100% + 10px);
				z-index: 10;
				width: max-content;
				max-width: 240px;
				padding: 8px 10px;
				border-radius: @radiusLg;
				border: 1px solid @guidedRoseBorder;
				background: #fff;
				color: @guidedRose;
				font-size: 12px;
				font-weight: 600;
				text-transform: none;
				letter-spacing: normal;
				line-height: 1.5;
				.elevation.floatingPanel();
				opacity: 0;
				visibility: hidden;
				transform: translate(0, -4px);
				transition:
					opacity 0.12s ease,
					transform 0.12s ease,
					visibility 0s linear 0.12s;
				pointer-events: none;

				&::before {
					content: "";
					position: absolute;
					top: -6px;
					left: 12px;
					width: 10px;
					height: 10px;
					background: #fff;
					border-left: 1px solid @guidedRoseBorder;
					border-top: 1px solid @guidedRoseBorder;
					transform: rotate(45deg);
				}
			}

			&:hover .date-interest-tooltip,
			&:focus .date-interest-tooltip,
			&:focus-within .date-interest-tooltip {
				opacity: 1;
				visibility: visible;
				transform: translate(0, 0);
				transition:
					opacity 0.12s ease,
					transform 0.12s ease,
					visibility 0s;
			}
		}
	}

	&.full {
		box-shadow: none;
		padding: 0 0 15px;
		overflow: visible;

		.brand-layout {
			.brand-layout__sections {
				padding-top: 0;
			}
		}

		.brand-top {
			padding-top: 12px;
		}

		.brand-logo {
			width: 40%;
			&.is-logo {
				width: 60%;
			}
			&.white {
				width: 40%;
			}
		}

		.brand-top {
			padding: 0;
			.brand-logo {
				width: 100%;

				&.is-logo,
				&.white {
					width: 100%;
				}
			}
		}

		.brand-meta-panel {
			margin-left: 0;
			margin-right: 0;
		}

		.content {
			padding: 0;
		}
	}

	.illustration-only {
		font-size: 10px;
		position: absolute;
		padding: 0;
		margin: 0;
		bottom: 0;
		left: 0;
		width: 100%;
		text-align: center;
	}

	.ban {
		width: 0.9em;
	}

	.content {
		padding: 20px 20px 0;

		.name {
			text-align: center;
			font-size: 22px;
			margin-bottom: 10px;
			color: @guidedRose;

			&.standard {
				font-weight: 400;
			}

			.hide-on-mobile {
				color: inherit;
			}

			a {
				color: inherit;
				text-decoration: none;

				&:hover {
					text-decoration: underline;
				}

				&.disabled {
					cursor: default;

					&:hover {
						text-decoration: none;
					}
				}
			}
		}

		.more-about-wrap {
			margin-bottom: 10px;
			text-align: center;
		}

		.about {
			.opacity(0.85);
		}

		hr {
			border-color: fade(@formDefaultColour, 25%);
			margin: 18px 0;
		}

		p {
			margin: 0 0 10px;
			font-size: 15px;

			&.brand-description {
				margin: 0 0 15px;
			}

			.label {
				font-weight: @bold;
			}

			span {
				&.description {
					font-size: 0.9em;
					font-style: italic;
					line-height: 1.4;

					.more {
						color: @formDefaultColour;
						cursor: pointer;
						&:hover {
							text-decoration: underline;
						}
					}
				}
			}
		}

		.followings {
			.following {
				margin-top: 10px;

				&:first-child {
					margin-top: 0;
				}

				.icon-wrap {
					display: inline-block;
					width: 25px;
					height: 20px;
					vertical-align: middle;
					margin-right: 5px;
					text-align: center;

					.svg {
						height: 20px;
						display: inline-block;
						width: auto;

						&.disabled {
							filter: grayscale(100%);
						}
					}
				}

				.label {
					display: inline-block;
					vertical-align: middle;
					.opacity(0.85);
					padding-top: 4px;
					font-size: 15px;

					&.social-link {
						color: inherit;
						text-decoration: underline;
						cursor: pointer;
						text-decoration-color: @formDefaultColour;

						span {
							cursor: pointer !important;
						}

						&:hover {
							color: @guidedRose;
						}
					}

					a {
						color: inherit;
						text-decoration: underline;

						&:hover {
							color: @guidedRose;
						}
					}
				}
			}
		}

		&.connected {
			p {
				padding: 0;
				margin: 10px 0 20px;

				&.connected-opener {
					border: 2px solid @formDefaultColour;
					background-color: @formDefaultColour;
					color: #fff;
					.rounded(@radiusMd);
					padding: 20px;
				}
			}

			img {
				width: 100%;
				margin: 0 auto;
				display: block;
				.rounded(@radiusLg);
			}
		}

		&.brand-secondary-content--whats-next {
			h1 {
				font-size: 24px;
				margin: 0 0 20px;
				color: @guidedRose;
				font-weight: normal;

				em {
					font-style: normal;
					font-weight: @bold;
				}

				.req-from-text {
					font-size: 0.6em;
					display: block;
					color: inherit;
				}
			}

			h2 {
				font-size: 22px;

				&.standard {
					font-weight: 400;
				}
			}

			p {
				margin-bottom: 20px;

				&.view {
					cursor: pointer;

					&:hover {
						text-decoration: underline;
					}
				}
			}

			.connect-message {
				padding: 0;
				margin: 10px 10px;

				.heading-btn {
					display: flex;
					justify-content: space-between;
					margin: 0 0 10px;

					h2 {
						margin: 0 30px 0 0;
					}
				}

				h2 {
					font-size: 18px;
					margin: 0 40px 0 0;
				}

				p {
					font-size: 16px;
					margin: 0 30px 20px 0;

					strong {
						&.form-default {
							color: @formDefaultColour;
						}
					}

					.info {
						color: @guidedRose;
						font-weight: bold;
						text-decoration: underline;
						cursor: pointer;
						font-style: italic;

						&:hover {
							text-decoration: none;
						}
					}
				}
			}

			.limited-no-subscription {
				background: @formDefaultColour;
				padding: 20px;
				.rounded(@radiusLg);
				margin-top: 15px;

				p {
					color: #fff;
					margin: 0 0 15px;
					font-size: 1.2em;

					em {
						font-style: normal;
						text-decoration: underline;
					}
				}
			}
		}
	}

	.options {
		margin-top: 25px;
	}

	.quick-brief-footer {
		padding: 18px 20px 0;
		border-top: 1px solid fade(@formDefaultColour, 18%);
		margin-top: 18px;

		&.full {
			padding-left: 0;
			padding-right: 0;
		}
	}

	.quick-brief-footer__actions {
		display: flex;
		justify-content: flex-start;
	}

	.quick-brief-footer__button {
		min-width: 200px;
	}

	.quick-brief-footer__list {
		margin-top: 14px;
	}

	&.disabled {
		.match {
			color: #000;
		}
	}
}
.hide-on-desktop {
	display: none;
}
@media screen and (max-width: 767px) {
	.brand {
		.connected-info {
			.connected-brand-summary {
				margin-bottom: 16px;

				.brand-top {
					gap: 14px;
				}

				.img-wrap-outer {
					width: 112px;
				}

				.brand-top-info {
					font-size: 24px;
				}
			}

			.img-contact {
				display: block;
			}

			.connect-with-info {
				padding: 18px 20px;
				&__name {
					font-size: 21px;
				}

				&__email {
					font-size: 17px;
				}
			}
		}

		&.has-top-label {
			.brand-layout__sections {
				padding-top: 30px;
			}
		}
		.brand-meta-panel {
			margin: 20px 20px 14px;
			&__active-pitch {
				align-items: flex-start;
			}

			&__active-pitch-image {
				width: 78px;
				height: 78px;
			}
		}

		.brand-top {
			flex-direction: column;
			align-items: flex-start;
			gap: 18px;
			margin-bottom: 8px;

			.img-wrap-outer {
				width: 128px;
				padding: 10px;
			}

			.brand-top-info {
				width: 100%;

				.hide-on-mobile {
					display: none;
				}

				.name {
					.req-from-text {
						margin-top: 26px;
					}
				}
			}
		}

		.hide-on-mobile {
			display: none;
		}

		.content {
			padding: 20px 20px 0;
		}

		&.full {
			.content {
				.name {
					.hide-on-mobile {
						display: none;
					}
				}
			}
		}
		.illustration-only {
			position: absolute;
			bottom: 50%;
			left: calc(100% + 20px);
			text-align: left;
			transform: translateY(50%);
		}
	}
	.hide-on-desktop {
		display: block;
	}
}

@media screen and (min-width: 768px) {
	.brand {
		.brand-top {
			.brand-top-info {
				.name {
					.hide-on-mobile {
						color: inherit;
					}
				}
			}
		}

		.top-label {
			text-align: center;
		}

		&.full,
		&.landscape {
			display: flex;
			flex-direction: column;

			.brand-layout {
				display: flex;
				flex-direction: column;
				width: 100%;
				height: 100%;
				flex-grow: 1;
			}

			.brand-layout__sections {
				flex-grow: 1;
				min-height: 100%;
				display: grid;
				grid-template-columns: fit-content(420px) minmax(0, 1fr);
				column-gap: 24px;
				align-items: start;
				align-content: start;
			}

			.expires,
			.brand-top,
			.brand-meta-panel-row,
			.brand-meta-panel {
				grid-column: 1 / -1;
			}

			.expires {
				justify-self: end;
				align-self: start;
			}

			.brand-primary-content {
				grid-column: 1;
			}

			.brand-secondary-content {
				grid-column: 2;
			}

			.opening-hr {
				display: none;
			}

			.brand-meta-panel-row {
				display: flex;
				flex-wrap: wrap;
				gap: 20px;

				.brand-meta-panel {
					display: inline-block;
					flex: 1 1 0;
					min-width: 300px;
					max-width: none;
					margin: 0 0 22px;

					&.doi {
						display: block;
						align-items: stretch;
					}
				}
			}
		}

		.content {
			&.brand-secondary-content--whats-next {
				padding: 0 12px;
				margin: 0;

				h1 {
					margin-right: 20px;
					margin-bottom: 10px;
				}
				.connect-message {
					.heading-btn {
						display: flex;
						justify-content: space-between;
						align-items: center;
						gap: 10px;

						h2 {
							margin: 0;
						}
						.button {
							margin: 0;
						}
					}
					p {
						margin: 0;
						padding: 0;
						font-size: 16px;
						line-height: 1.5;
						text-align: left;
						margin-top: 10px;
					}
				}
			}

			.name {
				text-align: left;

				.req-from-text {
					font-size: 0.6em;
					display: block;
				}
			}
		}

		.connected-info {
			.img-contact {
				display: flex;
				align-items: flex-start;
				gap: 3%;

				.connected-brand-summary {
					width: 50%;
					max-width: 400px;
				}

				.connect-with-info {
					margin: 0;
					flex-grow: 1;
					min-width: 0;
				}
			}
		}

		&.landscape {
			.brand-top {
				padding: 0 20px;
				margin: 0 0 22px;
			}

			.brand-meta-panel {
				margin: 0 20px 18px;
				display: inline-block;
				min-width: 300px;
			}

			.brand-meta-panel-row {
				margin: 0 20px;

				.brand-meta-panel {
					margin: 0 0 18px;
				}
			}

			.brand-primary-content,
			.brand-secondary-content {
				padding: 0 20px;
			}
			&.has-options {
				position: relative;
				padding-bottom: 30px;
			}

			.options {
				margin-top: auto;
				padding-top: 24px;
			}
		}

		&.full {
			.brand-layout__sections {
				grid-template-columns: fit-content(520px) minmax(0, 1fr);
				column-gap: 30px;
			}

			.brand-top {
				padding: 14px 0 0;
				margin: 0 0 22px;
			}

			&.tabbed {
				.brand-top {
					padding-top: 0;
				}
			}

			.brand-meta-panel {
				margin: 0 0 22px;
			}

			.brand-primary-content {
				max-width: 45vw;
			}

			.brand-primary-content,
			.brand-secondary-content {
				padding: 0;
			}

			.brand-meta-panel-row .brand-meta-panel:only-child {
				//flex-basis: 100%;
			}
		}
	}
}

@media screen and (min-width: 1024px) {
	.brand {
		&.full {
			.brand-meta-panel-row {
				gap: 40px;
			}
		}
	}
}
@media screen and (min-width: 1200px) {
	.brand {
		.content {
			.name {
				font-size: 20px;
			}
		}
	}
}

.just-happened-overlay {
	position: absolute;
	inset: 0;
	z-index: 3;
	background-color: @surfacePanel;
	border-radius: inherit;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 16px;
	padding: 30px 20px;

	.just-happened-overlay-inner {
		padding: 20px 20px 25px;
		border-radius: @radiusXl;
	}

	.brand-top {
		display: block;
		padding: 0;
		margin: 0;

		.img-wrap-outer {
			width: 110px;
			padding: 12px;
			background: #fff;
			border-radius: @radiusXl;
			margin: 0 auto;
			.elevation.card();
		}
	}

	.just-happened-message {
		font-size: 18px;
		line-height: 1.5;
		text-align: center;
		margin: 0;
		padding: 0;
	}
}

.tags-section {
	margin: 15px 0;
	padding: 0;
}

.tags-header {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content: flex-start;
	gap: 10px;
	width: fit-content;
	max-width: 100%;
	min-width: 0;

	.tags-wrap {
		flex: 0 1 auto;
		min-width: 0;
	}
}

.tags-wrap {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	width: fit-content;
	max-width: 220px;
	min-width: 0;
}

.edit-tags-button {
	background-color: transparent !important;
	border: 1px solid @formDefaultColour !important;
	color: @formDefaultColour !important;
	padding: 4px 10px !important;
	font-size: 12px !important;
	white-space: nowrap;
	display: flex;
	align-items: center;
	gap: 5px;

	.edit-icon {
		width: 14px;
		height: 14px;
	}

	&:hover {
		background-color: @formDefaultColour !important;
		color: white !important;

		.svg {
			filter: brightness(0) invert(1);
		}
	}
}

.tag-button {
	position: relative;
	padding: 6px 12px !important;
	border: 2px solid;
	border-radius: @radiusPill;
	background: transparent;
	cursor: pointer;
	font-size: 12px !important;
	font-weight: @bold;
	transition: all 0.2s ease;
	outline: none;
	text-transform: none;
	color: white !important;
	display: flex;
	align-items: center;
	gap: 2px;
	padding: 4px 10px 4px 4px !important;

	svg {
		width: 16px;
		aspect-ratio: 1/1;
		fill: white;
	}

	&.has-remove {
		padding-right: 30px !important;
	}

	&:hover {
		transform: scale(1.05);
	}

	.remove-tag {
		position: absolute;
		right: 6px;
		top: 50%;
		transform: translateY(-50%);
		width: 18px;
		height: 18px;
		line-height: 18px;
		text-align: center;
		border-radius: @radiusCircle;
		background-color: rgba(255, 255, 255, 0.2);
		color: white;
		font-size: 14px;
		cursor: pointer;
		transition: background-color 0.2s ease;

		&:hover {
			background-color: rgba(255, 255, 255, 0.4);
		}
	}
}
</style>
